import { HTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';
import styles from './styles.module.scss';

export interface ITableColumn<T = Record<string, unknown>> {
  key: string;
  header: string;
  render?: (row: T, index: number) => ReactNode;
  className?: string;
}

export interface ITableRow<T = Record<string, unknown>> {
  id: string | number;
  data: T;
}

export interface ITableProps<T = Record<string, unknown>>
  extends HTMLAttributes<HTMLDivElement> {
  columns: ITableColumn<T>[];
  rows: ITableRow<T>[];
  onRowClick?: (row: ITableRow<T>, index: number) => void;
}

export const Table = <T,>({
  columns,
  rows,
  onRowClick,
  className,
  ...rest
}: ITableProps<T>) => {
  const getCellContent = (
    column: ITableColumn<T>,
    row: ITableRow<T>,
    index: number
  ): ReactNode => {
    if (column.render) {
      return column.render(row.data, index);
    }
    const value = (row.data as Record<string, unknown>)[column.key];
    return value != null ? String(value) : '';
  };

  return (
    <div className={clsx(styles.tableWrapper, className)} {...rest}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={clsx(styles.headerCell, column.className)}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.id}
              className={clsx(styles.row, {
                [styles.clickable]: onRowClick,
              })}
              onClick={() => {
                if (onRowClick) {
                  onRowClick(row, index);
                }
              }}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={clsx(styles.cell, column.className)}
                >
                  {getCellContent(column, row, index)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
